/* eslint-disable camelcase */
import { QuestionTypes } from '@hotjar/feedback/domain/Surveys/Questions/QuestionTypes';
import type { SurveyQuestion } from '../../../../../domain/Surveys/Questions';

export type QuestionBankQuestion = Partial<Exclude<SurveyQuestion, 'type' | 'text'>> &
  Pick<SurveyQuestion, 'type' | 'text'>;

export type QuestionCategory = {
  category_name: string;
  questions: QuestionBankQuestion[];
};

export const QUESTIONS_BY_CATEGORY: QuestionCategory[] = [
  {
    category_name: 'USER TYPE: Site Users',
    questions: [
      {
        text: 'Where exactly did you first hear about us?',
        type: QuestionTypes.SINGLE_OPEN_ENDED_SINGLE_LINE,
      },
      {
        text: 'How did you find our site?',
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
      {
        text: "What's missing on this page?",
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
      {
        text: 'Were you able to find the information you were looking for?',
        type: QuestionTypes.SINGLE_CLOSE_ENDED,
        randomize_answer_order: false,
        answers: [
          { text: 'Yes', comments: false },
          { text: "No (please tell us what's missing)", comments: true },
        ],
      },
      {
        text: 'What other information would you like to see on this page?',
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
      {
        text: 'Is there anything preventing you from signing up at this point?',
        type: QuestionTypes.SINGLE_CLOSE_ENDED,
        randomize_answer_order: false,
        answers: [
          { text: 'Yes (please tell us what)', comments: true },
          { text: 'No', comments: false },
        ],
      },
      {
        text: 'Is there anything preventing you from purchasing at this point?',
        type: QuestionTypes.SINGLE_CLOSE_ENDED,
        randomize_answer_order: false,
        answers: [
          { text: 'Yes (please tell us what)', comments: true },
          { text: 'No', comments: false },
        ],
      },
      {
        text: 'If you could change anything on this page, what would you have us do?',
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
      {
        text: 'What could we do to make this site more useful?',
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
      {
        text: "Is there anything on this page that doesn't work the way you expected it to?",
        type: QuestionTypes.SINGLE_CLOSE_ENDED,
        randomize_answer_order: false,
        answers: [
          { text: 'Yes (please tell us what)', comments: true },
          { text: 'No', comments: false },
        ],
      },
    ],
  },
  {
    category_name: 'USER TYPE: Customers ',
    questions: [
      {
        text: "What's the one thing that nearly stopped you from buying from us?",
        type: QuestionTypes.SINGLE_OPEN_ENDED_SINGLE_LINE,
      },
      {
        text: 'What would persuade you to use us more often?',
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
      {
        text: 'How likely are you to recommend us to a friend or colleague?',
        type: QuestionTypes.NET_PROMOTER_SCORE,
        labels: [{ text: 'Not likely at all' }, { text: 'Extremely likely' }],
      },
      {
        text: "What's the biggest question you had about our pricing?",
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
      {
        text: 'How would you rate our service on a scale of 0 - 10?',
        type: QuestionTypes.NET_PROMOTER_SCORE,
        labels: [{ text: 'Horrible' }, { text: 'Fantastic' }],
      },
      {
        text: 'How would you rate our support on a scale of 0 - 10?',
        type: QuestionTypes.NET_PROMOTER_SCORE,
        labels: [{ text: 'Horrible' }, { text: 'Fantastic' }],
      },
      {
        text: 'What was your biggest fear or concern about purchasing from us?',
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
    ],
  },
  {
    category_name: 'USER TYPE: Leads (Non–customers)',
    questions: [
      {
        text:
          "Describe yourself in a few words (e.g. I'm a Male 26 yr old designer with a " +
          'passion for cars)',
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
      {
        text: 'Is our pricing clear?',
        type: QuestionTypes.SINGLE_CLOSE_ENDED,
        randomize_answer_order: false,
        answers: [
          { text: 'Yes', comments: false },
          { text: "No (please tell us what's unclear)", comments: true },
        ],
      },
    ],
  },
  {
    category_name: 'ORGANIZATION TYPE: E-commerce',
    questions: [
      {
        text: 'Was there anything about this checkout process that we should improve?',
        type: QuestionTypes.SINGLE_CLOSE_ENDED,
        randomize_answer_order: false,
        answers: [
          { text: 'Yes (please tell us what)', comments: true },
          { text: 'No', comments: false },
        ],
      },
      {
        text: 'If you did not make a purchase today, what was it that stopped you?',
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
      {
        text: 'Do you have any questions before you complete your purchase?',
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
    ],
  },
  {
    category_name: 'ORGANIZATION TYPE: Software / SaaS',
    questions: [
      {
        text: 'What convinced you to pay for this service?',
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
      {
        text: 'Was this help section useful?',
        type: QuestionTypes.SINGLE_CLOSE_ENDED,
        randomize_answer_order: false,
        answers: [
          { text: 'Yes', comments: false },
          { text: 'No (please tell us why not)', comments: true },
        ],
      },
      {
        text: 'Was this article useful?',
        type: QuestionTypes.SINGLE_CLOSE_ENDED,
        randomize_answer_order: false,
        answers: [
          { text: 'Yes', comments: false },
          { text: 'No (please tell us why not)', comments: true },
        ],
      },
      {
        text: 'What persuaded you to start a trial?',
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
      {
        text: "What's one feature we can add that would make our product indispensable for you?",
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
    ],
  },
  {
    category_name: 'ORGANIZATION TYPE: Publisher / Blog',
    questions: [
      {
        text: 'What topics would you like to see us write about next?',
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
      {
        text: 'What other content would you like to see us offer?',
        type: QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE,
      },
    ],
  },
];
